import { isCapacitor } from "./capacitor";
import { enablePushNotifications } from "./pwa";
import { getFcmPermissionStatus } from "./fcm";
import { meldingStand, metTijdslimiet, type MeldingStand } from "./meldingen";
import { nativePlugin } from "./nativeplugin";

/*
 * De rechten die de WebView nodig heeft: microfoon voor spraakberichten,
 * locatie voor het delen van een plek.
 *
 * Ze worden normaal gevraagd op het moment dat je de knop indrukt, en dat is de
 * juiste volgorde - een dialoog met context wordt vaker toegestaan dan een
 * dialoog uit het niets. Maar het betekent ook dat niemand doorheeft dat ze weg
 * zijn: je merkt het pas als je de microfoonknop indrukt en er gebeurt niets.
 *
 * En weg kunnen ze raken zonder dat iemand iets deed. Een verwijdering van de
 * app wist alle verleende rechten - precies wat hier op 07-09-2026 gebeurde bij
 * het wisselen van ondertekensleutel - en Android trekt rechten vanzelf in bij
 * een app die je maanden niet opent.
 *
 * Vandaar een controle bij de start, en een scherm waar de stand van alles bij
 * elkaar staat.
 */

export type RechtStand = "granted" | "denied" | "prompt" | "prompt-with-rationale" | "onbekend";

export type Rechten = {
  microfoon: RechtStand;
  locatie: RechtStand;
};

const ONBEKEND: Rechten = { microfoon: "onbekend", locatie: "onbekend" };

interface SwiftRechtenPlugin {
  stand(): Promise<Rechten>;
  vraag(opties: { alias: "microfoon" | "locatie" }): Promise<Rechten>;
}

function rechtenPlugin(): SwiftRechtenPlugin | null {
  if (!isCapacitor()) return null;
  return nativePlugin<SwiftRechtenPlugin>("SwiftRechten");
}

/** Buiten de Android-app regelt de browser dit zelf, per keer dat je iets
 *  gebruikt; daar valt niets vooraf te controleren. */
export async function leesRechten(): Promise<Rechten> {
  const p = rechtenPlugin();
  if (!p) return ONBEKEND;
  return p.stand();
}

export async function vraagRecht(alias: "microfoon" | "locatie"): Promise<Rechten> {
  const p = rechtenPlugin();
  if (!p) return ONBEKEND;
  return p.vraag({ alias });
}

/** Nog nooit gevraagd - dan heeft vragen zin. Bij "denied" niet: Android toont
 *  die dialoog niet meer en antwoordt meteen met nee. */
export function magVragen(stand: RechtStand): boolean {
  return stand === "prompt" || stand === "prompt-with-rationale";
}

/*
 * De ronde bij de eerste start.
 *
 * Eén keer per installatie, en alleen voor wat nog nooit gevraagd is. Meldingen
 * eerst, want dat is het enige dat de app stilletjes onbruikbaar maakt; daarna
 * microfoon en locatie. Eén dialoog tegelijk, want Android toont er ook maar
 * één tegelijk.
 *
 * Daarna nooit meer uit onszelf. Wie iets weigert heeft dat gemeend, en een app
 * die het bij elke start opnieuw vraagt is een app die je wegdrukt zonder te
 * lezen. Wat er ontbreekt staat vanaf dan in Instellingen, tab Rechten.
 */
/* De sleutel draagt een nummer omdat 1.19 de vlag zette voordat er iets was
   gebeurd. Op elk toestel dat die versie draaide staat hij nu aan terwijl er
   niets is gevraagd; een nieuwe sleutel geeft die toestellen hun ronde alsnog. */
const RONDE_GEDAAN = "swift_rechtenronde_gedaan_2";

export function rondeGedaan(): boolean {
  try {
    return localStorage.getItem(RONDE_GEDAAN) === "1";
  } catch {
    return false;
  }
}

function markeerRonde(): void {
  try {
    localStorage.setItem(RONDE_GEDAAN, "1");
  } catch {
    /* Privémodus of geblokkeerde opslag: dan doen we de ronde nog een keer. */
  }
}

let ronde = false;

export async function rechtenRonde(): Promise<void> {
  if (!isCapacitor() || rondeGedaan() || ronde) return;
  ronde = true;
  try {
    try {
      if (magVragen((await getFcmPermissionStatus()) as RechtStand)) {
        await enablePushNotifications();
      }
    } catch {
      /* Een mislukte vraag mag de volgende niet tegenhouden. */
    }

    /* Lukt dit niet, dan gaan we hieronder niet vragen én markeren we de ronde
       niet als gedaan. Een ronde die niets kon lezen is geen ronde - dat was de
       fout in 1.19, waar de vlag vooraf werd gezet en de kans daarmee verkeken
       was. */
    let stand = await leesRechten();

    for (const alias of ["microfoon", "locatie"] as const) {
      if (!magVragen(stand[alias])) continue;
      try {
        stand = await vraagRecht(alias);
      } catch {
        /* volgende */
      }
    }

    markeerRonde();
  } catch {
    /* Volgende start opnieuw proberen. */
  } finally {
    ronde = false;
  }
}

/*
 * Alles wat het instellingenscherm nodig heeft, in één keer en met een
 * tijdslimiet. Deze functie kan niet mislukken en kan niet blijven hangen: in
 * het slechtste geval weet hij iets niet, en zegt hij dat erbij.
 */
export type VolledigeStand = {
  melding: MeldingStand;
  rechten: Rechten;
  rechtenFout: string | null;
};

export async function volledigeStand(): Promise<VolledigeStand> {
  const [melding, r] = await Promise.all([
    meldingStand(),
    metTijdslimiet("rechten", leesRechten),
  ]);
  return {
    melding,
    rechten: r.ok ? r.waarde : ONBEKEND,
    rechtenFout: r.ok ? null : r.fout,
  };
}
